/**
 * 卡片展示模式
 *
 * 特点：
 * - 大缩略图
 * - 视觉丰富
 * - 适合图片类收藏
 * - 使用React.memo优化性能
 */

import {
  Check,
  Download,
  Globe,
  Folder,
  Pencil,
  Trash2,
  Inbox,
  ImageIcon,
} from "lucide-react";
import { memo, useEffect, useLayoutEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import type { MouseEventHandler } from "react";

import { getFolderNameById, getItemTagNames, getSourceName, mineApi, useFolders } from "@/modules/mine";
import type { Item, ItemTagData } from "@/modules/mine/types";
import { formatFullDateTime, formatRelativeTime } from "@/shared/lib/date";
import { cn } from "@/shared/lib/utils";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/shared/ui/tooltip";

import { TextCardPreview } from "./text-card-preview";

interface ItemCardProps {
  item: Item;
  onClick?: MouseEventHandler<HTMLDivElement>;
  onEdit?: () => void;
  onDelete?: () => void;
  actions?: ReactNode;
  selectionMode?: boolean;
  isSelected?: boolean;
  onStartSelection?: MouseEventHandler<HTMLButtonElement>;
}

type ItemTypeCardProps = ItemCardProps & {
  folderName: string;
};

const WEBSITE_FALLBACK_THEME = {
  bg: "bg-[#eceef2] dark:bg-[#202124]",
  initial: "rgba(198, 202, 210, 0.42)",
  line: "rgba(174, 180, 190, 0.78)",
};

function OverflowTooltipText({
  children,
  className,
}: {
  children: string;
  className?: string;
}) {
  const textRef = useRef<HTMLSpanElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useLayoutEffect(() => {
    const element = textRef.current;

    if (!element) {
      return;
    }

    const updateOverflow = () => {
      setIsOverflowing(
        element.scrollWidth > element.clientWidth + 1 ||
        element.scrollHeight > element.clientHeight + 1,
      );
    };

    updateOverflow();

    const observer = new ResizeObserver(updateOverflow);
    observer.observe(element);

    return () => observer.disconnect();
  }, [children]);

  const text = (
    <span ref={textRef} className={className}>
      {children}
    </span>
  );

  return (
    <Tooltip>
      <TooltipTrigger asChild>{text}</TooltipTrigger>
      {isOverflowing && (
        <TooltipContent
          sideOffset={6}
          className="max-w-xs whitespace-normal break-all text-left"
          style={{ textWrap: "wrap" }}
        >
          {children}
        </TooltipContent>
      )}
    </Tooltip>
  );
}

function CardCreatedAt({
  value,
  className,
}: {
  value?: string;
  className?: string;
}) {
  const relativeTime = formatRelativeTime(value) || "未知时间";
  const fullTime = formatFullDateTime(value) || relativeTime;

  return (
    <span
      className={cn(
        "shrink-0 text-[12px] text-muted-foreground/80 transition-opacity duration-150 group-hover/selection-item:opacity-0 group-data-[selection-mode=true]:opacity-0",
        className,
      )}
      title={fullTime}
    >
      {relativeTime}
    </span>
  );
}

function CardSurface({
  children,
  className,
  onClick,
  selectionMode = false,
  isSelected = false,
  selectionIndicatorClassName,
}: {
  children: ReactNode;
  className?: string;
  onClick?: MouseEventHandler<HTMLDivElement>;
  selectionMode?: boolean;
  isSelected?: boolean;
  selectionIndicatorClassName?: string;
}) {
  return (
    <div
      data-selection-mode={selectionMode}
      onClick={onClick}
      className={cn(
        "group relative h-full cursor-pointer overflow-hidden rounded-[20px] bg-card shadow-[0_8px_24px_rgba(0,0,0,0.03)] transition-all duration-300 ease-out will-change-transform hover:-translate-y-1 hover:shadow-[0_16px_40px_rgba(0,0,0,0.08)]",
        className,
      )}
    >
      {children}
      {selectionMode && (
        <>
          <div
            className={cn(
              "pointer-events-none absolute inset-0 z-30 rounded-[20px] ring-inset transition-colors duration-200",
              isSelected ? "ring-2 ring-primary/70" : "ring-1 ring-transparent",
            )}
          />
          <div
            className={cn(
              "pointer-events-none absolute right-3 bottom-5 z-40 flex size-5 items-center justify-center rounded-md border-[1.5px] backdrop-blur transition-colors duration-200",
              isSelected
                ? "border-primary bg-primary text-primary-foreground"
                : "border-border/70 bg-background/90 text-transparent",
              selectionIndicatorClassName,
            )}
          >
            <Check className="size-3" strokeWidth={2.6} />
          </div>
        </>
      )}
    </div>
  );
}

function getCardHeaderBg(hasCoverImage: boolean) {
  if (hasCoverImage) {
    return "bg-[#f8fafc] dark:bg-[#18181b]";
  }

  return "bg-[#eceef2] dark:bg-[#202124]";
}

function getSafeHostname(url?: string) {
  if (!url) {
    return "website";
  }

  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return "website";
  }
}

function CardFolder({
  folderName,
  className = "",
}: {
  folderName: string;
  className?: string;
}) {
  return (
    <div
      className={`flex items-center gap-1.5 text-[12px] uppercase tracking-wider text-muted-foreground/80 ${className}`}
    >
      {folderName !== "未分类" ? <Folder size={14} /> : <Inbox size={14} />}
      <span className="truncate max-w-[120px]">{folderName}</span>
    </div>
  );
}

function CardTags({
  tags,
  limit = 3,
}: {
  tags?: ItemTagData[];
  limit?: number;
}) {
  const normalizedTags = getItemTagNames(tags);

  if (normalizedTags.length === 0) {
    return null;
  }

  const remainingTags = normalizedTags.slice(limit);
  const remainingCount = Math.max(normalizedTags.length - limit, 0);

  return (
    <div className="flex min-w-0 flex-nowrap items-center gap-2 overflow-hidden">
      {normalizedTags.slice(0, limit).map((tag) => (
        <span
          key={tag}
          className="inline-flex min-w-0 max-w-[72px] shrink items-center overflow-hidden rounded-md bg-muted px-2 py-1 text-[11px] font-medium text-muted-foreground"
        >
          <OverflowTooltipText className="min-w-0 truncate" children={`#${tag}`} />
        </span>
      ))}
      {remainingCount > 0 && (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="shrink-0 self-center text-[11px] text-muted-foreground">
              +{remainingCount}
            </span>
          </TooltipTrigger>
          <TooltipContent
            sideOffset={6}
            className="max-w-xs whitespace-normal break-all text-left"
            style={{ textWrap: "wrap" }}
          >
            {remainingTags.map((tag) => `#${tag}`).join("、")}
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  );
}

function CardActionButtons({
  downloadUrl,
  onEdit,
  onDelete,
  variant = "light",
}: Pick<ItemCardProps, "onEdit" | "onDelete"> & {
  downloadUrl?: string;
  variant?: "light" | "dark";
}) {
  if (!downloadUrl && !onEdit && !onDelete) {
    return null;
  }

  const handleDownload = () => {
    if (!downloadUrl) {
      return;
    }

    window.open(downloadUrl, "_blank", "noopener,noreferrer");
  };

  const buttonClassName = cn(
    "flex size-7 items-center justify-center rounded-md backdrop-blur-md transition-colors",
    variant === "dark"
      ? "bg-black/50 text-white/90 hover:bg-black/70 hover:text-white"
      : "bg-muted/80 text-muted-foreground hover:bg-muted hover:text-foreground",
  );

  return (
    <div
      className="absolute top-3 right-3 z-30 flex items-center gap-1.5 opacity-0 transition-opacity duration-200 group-hover:opacity-100"
      onClick={(e) => e.stopPropagation()}
    >
      {downloadUrl && (
        <button
          className={buttonClassName}
          title="下载"
          onClick={handleDownload}
        >
          <Download size={14} strokeWidth={2.5} />
        </button>
      )}
      {onEdit && (
        <button
          className={buttonClassName}
          title="编辑"
          onClick={() => onEdit()}
        >
          <Pencil size={13} strokeWidth={2.5} />
        </button>
      )}
      {onDelete && (
        <button
          className={cn(
            buttonClassName,
            variant === "dark" ? "hover:text-[#ff453a]" : "hover:text-red-600",
          )}
          title="删除"
          onClick={() => onDelete()}
        >
          <Trash2 size={13} strokeWidth={2.5} />
        </button>
      )}
    </div>
  );
}

function getImageFormatLabel(item: Item) {
  const mimeSubtype = item.mime_type?.split("/").at(1);

  if (mimeSubtype) {
    return mimeSubtype.replace(/\+xml$/i, "").toUpperCase();
  }

  const extension = item.url?.split("?")[0]?.split(".").pop();
  return extension ? extension.toUpperCase() : "IMG";
}

function getImageResolutionLabel(item: Item) {
  if (!item.image?.width || !item.image?.height) {
    return "0 × 0";
  }

  return `${item.image.width} × ${item.image.height}`;
}

function getImageFileSizeLabel(item: Item) {
  if (!item.size) {
    return "Unknown";
  }

  const numericSize = Number(item.size);
  if (!Number.isFinite(numericSize) || numericSize <= 0) {
    return item.size;
  }

  const units = ["B", "KB", "MB", "GB"];
  let size = numericSize;
  let unitIndex = 0;

  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }

  return `${size >= 10 || unitIndex === 0 ? size.toFixed(0) : size.toFixed(1)} ${units[unitIndex]}`;
}

function getImagePaletteColors(item: Item) {
  return item.image?.visual?.palettes?.map((color) => color.hex).slice(0, 4) ?? [];
}

function ImagePaletteDots({ colors }: { colors: string[] }) {
  const displayColors = colors.length > 0
    ? colors
    : ["rgba(255,255,255,0.8)", "rgba(255,255,255,0.55)", "rgba(255,255,255,0.32)"];

  return (
    <div className="flex shrink-0 items-center gap-0.5">
      {displayColors.map((color, index) => (
        <span
          key={`${color}-${index}`}
          className="size-2.5 rounded-full"
          style={{ backgroundColor: color }}
        />
      ))}
    </div>
  );
}

function WebsiteFallbackPreview({
  hostname,
  source,
}: {
  hostname: string;
  source: string;
}) {
  const theme = WEBSITE_FALLBACK_THEME;
  const fallbackLetter = (source || hostname || "W").charAt(0).toUpperCase();

  return (
    <div className={cn("relative h-full w-full overflow-hidden", theme.bg)}>
      <div
        className="pointer-events-none absolute select-none font-serif text-[120px] font-black leading-none"
        style={{
          right: -8,
          bottom: -20,
          color: theme.initial,
          letterSpacing: "-4px",
        }}
      >
        {fallbackLetter}
      </div>

      <div className="pointer-events-none absolute inset-0 flex flex-col justify-center gap-[7px] pl-5 pr-20">
        {[72, 88, 55, 78].map((width, index) => (
          <div
            key={index}
            className="h-[3px] rounded-full"
            style={{
              width: `${width}%`,
              backgroundColor: theme.line,
              opacity: 0.6,
            }}
          />
        ))}
      </div>
    </div>
  );
}

function WebsiteItemCard({
  item,
  folderName,
  onClick,
  onEdit,
  onDelete,
  actions,
  selectionMode,
  isSelected,
}: ItemTypeCardProps) {
  const hasImage = !!item.website?.cover_url;
  const hostname = getSafeHostname(item?.url);
  const source = item?.url ? getSourceName(item.url) : "Website";
  const headerBg = getCardHeaderBg(hasImage);

  return (
    <CardSurface
      className="flex flex-col"
      onClick={onClick}
      selectionMode={selectionMode}
      isSelected={isSelected}
    >
      {!selectionMode && (actions ?? <CardActionButtons onEdit={onEdit} onDelete={onDelete} />)}

      <div data-slot="website-card-cover" className={`relative h-[120px] w-full shrink-0 overflow-hidden ${headerBg}`}>
        {hasImage ? (
          <div className="absolute inset-0">
            <img
              src={item.website?.cover_url}
              className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.025]"
              alt={item?.title}
            />
          </div>
        ) : (
          <WebsiteFallbackPreview hostname={hostname} source={source} />
        )}

        {hasImage && (
          <div className="absolute left-3 top-3">
            <div className="flex items-center gap-1.5 rounded-full bg-background/85 px-2 py-0.5 backdrop-blur-sm">
              {item.website?.favicon_url ? (
                <img
                  src={item.website.favicon_url}
                  className="size-3 object-contain"
                  alt=""
                />
              ) : (
                <Globe size={12} className="text-muted-foreground/60" />
              )}
              <span className="text-[10px] font-semibold text-foreground">{source}</span>
            </div>
          </div>
        )}
      </div>

      <div data-slot="website-card-body" className="flex min-h-0 flex-1 flex-col overflow-hidden p-5">
        <div className="mb-1.5 flex shrink-0 items-center gap-1.5">
          {item.website?.favicon_url ? (
            <img
              src={item.website.favicon_url}
              className="size-3.5 object-contain"
              alt=""
            />
          ) : (
            <Globe size={13} className="text-muted-foreground/60" />
          )}
          <span className="truncate text-[12px] font-semibold text-muted-foreground">
            {hostname}
          </span>
        </div>

        <OverflowTooltipText
          className="compact-website-card-title mb-1.5 line-clamp-2 shrink-0 text-[17px] font-bold leading-snug text-foreground"
          children={item?.title || hostname}
        />
        {item.note?.trim() ? (
          <OverflowTooltipText
            className="mb-3 line-clamp-1 shrink-0 text-[13px] leading-relaxed text-muted-foreground/80"
            children={item.note}
          />
        ) : null}

        <div className="mb-2 shrink-0 empty:hidden">
          <CardTags tags={item.tags} />
        </div>

        <div className="mt-auto flex shrink-0 items-center justify-between gap-2">
          <CardFolder folderName={folderName} />
          <CardCreatedAt value={item.created_at} />
        </div>
      </div>
    </CardSurface>
  );
}

function ImageItemCard({
  item,
  folderName,
  onClick,
  onEdit,
  onDelete,
  actions,
  selectionMode,
  isSelected,
  onStartSelection,
}: ItemTypeCardProps) {
  const [imageUrl, setImageUrl] = useState(item.url);
  const retriedImageUrlsRef = useRef(new Set<string>());
  const formatLabel = getImageFormatLabel(item);
  const resolutionLabel = getImageResolutionLabel(item);
  const fileSizeLabel = getImageFileSizeLabel(item);
  const paletteColors = getImagePaletteColors(item);
  const normalizedTags = getItemTagNames(item.tags);
  const createdAtLabel = formatRelativeTime(item.created_at) || "未知时间";
  const createdAtTitle = formatFullDateTime(item.created_at) || createdAtLabel;

  useEffect(() => {
    setImageUrl(item.url);
    retriedImageUrlsRef.current.clear();
  }, [item.id, item.url]);

  const refreshExpiredImage = async () => {
    if (!imageUrl || retriedImageUrlsRef.current.has(imageUrl)) {
      setImageUrl(undefined);
      return;
    }

    retriedImageUrlsRef.current.add(imageUrl);

    try {
      const latestItem = await mineApi.getItem(item.id, {
        silent: true,
        skipCancelToken: true,
      });

      setImageUrl(latestItem.url && latestItem.url !== imageUrl
        ? latestItem.url
        : undefined);
    } catch {
      setImageUrl(undefined);
    }
  };

  return (
    <CardSurface
      onClick={onClick}
      selectionMode={selectionMode}
      isSelected={isSelected}
      selectionIndicatorClassName="bottom-4"
    >
      {!selectionMode && (actions ?? (
        <CardActionButtons
          downloadUrl={imageUrl}
          onEdit={onEdit}
          onDelete={onDelete}
          variant="dark"
        />
      ))}

      {imageUrl ? (
        <img
          src={imageUrl}
          alt={item?.title}
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.025]"
          loading="lazy"
          onError={() => void refreshExpiredImage()}
        />
      ) : (
        <div className="flex h-full w-full flex-col items-center justify-center gap-2 bg-muted text-muted-foreground">
          <ImageIcon size={32} className="opacity-20" />
          <span className="text-[12px] font-bold uppercase tracking-wider opacity-50">
            图片加载失败
          </span>
        </div>
      )}

      <div
        className="absolute right-3 bottom-3 z-20 rounded-md bg-black/45 px-1.5 py-0.5 text-[10px] font-bold uppercase leading-none text-white backdrop-blur-sm transition-opacity duration-200 group-hover:pointer-events-none group-hover:opacity-0 group-data-[selection-mode=true]:opacity-0"
      >
        {formatLabel}
      </div>

      <div className="absolute top-3 left-3 z-20 opacity-0 transition-opacity duration-200 group-hover:opacity-100">
        <ImagePaletteDots colors={paletteColors} />
      </div>

      <div className="absolute inset-x-0 bottom-0 z-20 flex translate-y-2 flex-col justify-end bg-gradient-to-t from-black/85 via-black/50 to-transparent p-3 opacity-0 transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100 group-focus-within:translate-y-0 group-focus-within:opacity-100">
        <OverflowTooltipText
          className="mb-1.5 line-clamp-1 text-[13px] font-semibold text-white drop-shadow-sm"
          children={item?.title || "Untitled Image"}
        />

        <div className="mb-2.5 flex flex-wrap items-center gap-2 overflow-hidden">
          <div className="flex shrink-0 items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-white/60">
            {folderName !== "未分类" ? (
              <Folder size={10} strokeWidth={2.5} />
            ) : (
              <Inbox size={10} strokeWidth={2.5} />
            )}
            <span className="max-w-[72px] truncate">{folderName}</span>
          </div>

          {normalizedTags.length > 0 && (
            <div className="h-2.5 w-px shrink-0 bg-white/20" />
          )}

          {normalizedTags.length > 0 && (
            <div className="flex min-w-0 items-center gap-1.5 overflow-hidden">
              {normalizedTags.slice(0, 2).map((tag) => (
                <span
                  key={tag}
                  className="flex min-w-0 max-w-[96px] shrink items-center overflow-hidden rounded bg-white/12 px-1.5 py-px text-[10px] text-white/90 ring-1 ring-white/10 backdrop-blur-md"
                >
                  <span className="mr-0.5 shrink-0 text-white/40">#</span>
                  <OverflowTooltipText className="min-w-0 truncate" children={tag} />
                </span>
              ))}
              {normalizedTags.length > 2 && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="shrink-0 px-0.5 text-[10px] text-white/50">
                      +{normalizedTags.length - 2}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent
                    sideOffset={6}
                    className="max-w-xs whitespace-normal break-all text-left"
                    style={{ textWrap: "wrap" }}
                  >
                    {normalizedTags.slice(2).map((tag) => `#${tag}`).join("、")}
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-1.5 text-[11px] text-white/80">
            <span className="font-mono tabular-nums">{resolutionLabel}</span>
            <span className="size-1 rounded-full bg-white/40" />
            <span className="font-mono tabular-nums">{fileSizeLabel}</span>
            <span className="size-1 rounded-full bg-white/40" />
            <span title={createdAtTitle} className="tabular-nums">
              {createdAtLabel}
            </span>
          </div>

          {onStartSelection && (
            <button
              type="button"
              aria-label={`选择${item.title?.trim() || "图片"}`}
              className="flex size-5 shrink-0 items-center justify-center rounded-md border-[1.5px] border-border/70 bg-background/90 text-transparent backdrop-blur focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              onClick={onStartSelection}
            >
              <Check className="size-3" strokeWidth={2.6} />
            </button>
          )}
        </div>
      </div>
    </CardSurface>
  );
}

function TextItemCard({
  item,
  folderName,
  onClick,
  onEdit,
  onDelete,
  actions,
  selectionMode,
  isSelected,
}: ItemTypeCardProps) {
  return (
    <CardSurface
      className="compact-text-card flex flex-col p-5"
      onClick={onClick}
      selectionMode={selectionMode}
      isSelected={isSelected}
    >
      {!selectionMode && (actions ?? <CardActionButtons onEdit={onEdit} onDelete={onDelete} />)}

      <div className="flex min-h-0 flex-1 flex-col">
        <OverflowTooltipText
          className="compact-text-card-title mb-4 line-clamp-2 min-w-0 text-[18px] font-bold leading-snug text-foreground"
          children={item?.title || "Untitled Note"}
        />

        <div className="relative min-h-0 flex-1 overflow-hidden">
          <TextCardPreview itemId={item.id} note={item.note} />
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-b from-transparent to-card" />
        </div>

        <div className="mt-6">
          <CardTags tags={item.tags} />
        </div>
      </div>

      <div className="mt-6 flex items-center justify-between">
        <CardFolder folderName={folderName} />
        <CardCreatedAt value={item.created_at} />
      </div>
    </CardSurface>
  );
}

export const ItemCard = memo((props: ItemCardProps) => {
  const folders = useFolders();
  const folderName = getFolderNameById(props.item.folder_id ?? "", folders);
  const cardProps = { ...props, folderName };

  switch (props.item.format) {
    case "website":
      return <WebsiteItemCard {...cardProps} />;
    case "image":
      return <ImageItemCard {...cardProps} />;
    case "text":
      return <TextItemCard {...cardProps} />;
    default:
      return null;
  }
});
